import { FC, useState } from "react";
import { ShortcutWidgetConfig, ShortcutItem } from "@/models/dashboard/WidgetConfig";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { ExternalLink, Link } from "lucide-react";

interface ShortcutWidgetProps {
  widgetId: string;
  name: string;
  config: ShortcutWidgetConfig;
}

// Component to handle favicon with fallback
const ShortcutIcon: FC<{ shortcut: ShortcutItem }> = ({ shortcut }) => {
  const [faviconError, setFaviconError] = useState(false);
  
  const isExternalUrl = (url: string) => {
    return url.startsWith("http://") || url.startsWith("https://");
  };

  // If we have a favicon URL and it hasn't errored, show the favicon
  if (shortcut.faviconUrl && !faviconError) {
    return (
      <img 
        src={shortcut.faviconUrl} 
        alt="" 
        className="h-5 w-5"
        onError={() => setFaviconError(true)}
      />
    );
  }

  // Fallback to icons
  if (isExternalUrl(shortcut.url)) {
    return <ExternalLink className="h-5 w-5" />;
  }
  return <Link className="h-5 w-5" />;
};

const ShortcutWidget: FC<ShortcutWidgetProps> = ({
  widgetId: _widgetId,
  name: _name,
  config,
}) => {
  const navigate = useNavigate();

  const isExternalUrl = (url: string) => {
    return url.startsWith("http://") || url.startsWith("https://");
  };

  const handleShortcutClick = (shortcut: ShortcutItem) => {
    if (isExternalUrl(shortcut.url)) {
      // Open external links in a new tab
      window.open(shortcut.url, "_blank", "noopener,noreferrer");
    } else {
      // Navigate to internal routes
      navigate(shortcut.url);
    }
  };

  if (config.shortcuts.length === 0) {
    return (
      <div className="text-sm text-muted-foreground text-center py-4">
        Ingen genveje tilføjet endnu.
        <br />
        <span className="text-xs">Klik på blyanten for at tilføje genveje.</span>
      </div>
    );
  }

  const isGridLayout = config.layout === "grid" || !config.layout;

  return (
    <div
      className={cn(
        "themed-scrollbar h-full overflow-auto p-2",
        isGridLayout ? "grid grid-cols-2 gap-2 auto-rows-min" : "flex flex-col gap-2"
      )}
    >
      {config.shortcuts.map((shortcut) => (
        <Button
          key={shortcut.id}
          variant="outline"
          className={cn(
            "justify-start gap-3 h-auto py-3",
            isGridLayout && "flex-col items-center text-center min-h-[80px]"
          )}
          onClick={() => handleShortcutClick(shortcut)}
        >
          <ShortcutIcon shortcut={shortcut} />
          <span className="text-sm">{shortcut.name}</span>
        </Button>
      ))}
    </div>
  );
};

export default ShortcutWidget;
